import React, { useEffect, useMemo, useState } from 'react';
import { datetime, dayjsToMoment, parseZone } from '@common/services/datetime';
import {
  INDEX_SHIFT_CONFIGS_QUERY,
  UPDATE_SHIFT_MUTATION,
  UPDATE_SHIFT_TAG_MUTATION,
} from '@scheduling/graphql';
import {
  ShiftDurationInput,
  StartDatetimeSelect,
  ShiftTagSelect,
} from '@scheduling/components';
import { FormInstance } from 'antd/lib/form';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation, useQuery } from '@common/hooks';
import { ShiftGapDetailsQueryCgResponse } from '@common/graphql/schema';
import { Banner, Form, Col, Row, Text } from '@common/components';

export function ShiftRequestEditContent({
  shift,
  setShiftEditMode,
  shiftGapEditForm,
  canEditShiftDetails,
  tagsEnabled,
  worksUser,
}: {
  shift: ShiftGapDetailsQueryCgResponse['shiftGap'];
  setShiftEditMode: (value: boolean) => void;
  shiftGapEditForm: FormInstance;
  canEditShiftDetails: boolean;
  tagsEnabled: boolean;
  worksUser: { id: string } | undefined;
}) {
  const [selectedStartTime, setSelectedStartTime] = useState<
    string | undefined
  >(undefined);

  const [updateShift] = useMutation(UPDATE_SHIFT_MUTATION, {
    onCompleted: () => {
      setShiftEditMode(false);
      createMessage('success', 'Shift has been updated!');
    },
    onError: () => {
      createMessage(
        'error',
        'Failed to update Shift. Please try again or contact your hiring partner.'
      );
    },
    refetchQueries: [
      'ShiftGapDetails',
      'PaginatedShiftGapOpenings',
      'ShiftsCountByDay',
      'SpearfishableWorkersByShiftGap',
      'SpearfishableGroupsByShiftGap',
    ],
  });

  const [updateShiftTag] = useMutation(UPDATE_SHIFT_TAG_MUTATION, {
    onCompleted: () => {
      setShiftEditMode(false);
      createMessage('success', 'Shift Tags has been updated!');
    },
    onError: () => {
      createMessage(
        'error',
        'Failed to update Shift Tag. Please try again or contact your hiring partner.'
      );
    },
    refetchQueries: ['ShiftGapDetails'],
  });

  const { data: dataShiftConfigs } = useQuery(INDEX_SHIFT_CONFIGS_QUERY, {
    variables: {
      filters: { role: shift.role?.id, unit: shift.organizationUnit.id },
    },
  });

  const organizationUnitShiftConfigs = useMemo(
    () =>
      dataShiftConfigs?.organizationUnitShiftConfigs?.map((config) => {
        const { startTime, endTime } = config;

        return {
          ...config,
          startTime: parseZone(startTime),
          endTime: parseZone(endTime),
        };
      }) || [],
    [dataShiftConfigs?.organizationUnitShiftConfigs]
  );

  useEffect(() => {
    // NOTE: datetime.tz reads a zoneless string as wall time in that zone;
    // datetime(x).tz(z) would read it in the runner's zone first.
    const localStartDateTime = datetime.tz(shift.startDatetime, shift.timeZone);
    const startDateTime = datetime.utc(
      localStartDateTime.format('YYYY-MM-DD HH:mm')
    );
    const startTime = startDateTime.format('HH:mm');
    const duration = Number(shift.shiftDuration);
    const durationValue =
      organizationUnitShiftConfigs.length > 0
        ? [duration.toFixed(2)]
        : [Math.floor(duration).toString(), duration.toFixed(2)];

    setSelectedStartTime(startTime);

    const values = {
      // NOTE: this value is both rendered by a raw antd v4 DatePicker and
      // submitted verbatim, so it has to cross as a Moment.
      startDate: dayjsToMoment(startDateTime),
      startTime: startTime,
      shiftDuration: durationValue,
    };

    shiftGapEditForm.setFieldsValue(values);
  }, [shiftGapEditForm, shift, organizationUnitShiftConfigs.length]);

  const handleSubmit = () => {
    const values = shiftGapEditForm.getFieldsValue();
    const duration =
      values.shiftDuration.length > 1
        ? values.shiftDuration[1]
        : values.shiftDuration[0];
    const shiftDuration = Number(duration).toFixed(2);

    const startTime = datetime(values.startTime, ['HH:mm']).format(
      'YYYY-MM-DDTHH:mm:ss'
    );
    const input = {
      organizationUnitId: shift.organizationUnit.id,
      roleId: shift.role?.id,
      shiftDuration: Number(shiftDuration),
      startDate: values.startDate,
      startTime,
      createdById: worksUser?.id || '',
      shiftGapTagIds: values.shiftGapTagIds,
    };

    if (canEditShiftDetails) {
      updateShift({
        variables: {
          id: shift.id,
          input,
        },
      });
    } else if (tagsEnabled) {
      updateShiftTag({
        variables: {
          id: shift.id,
          input: { shiftGapTagIds: values.shiftGapTagIds },
        },
      });
    }
  };

  const canEditProperties = shift.canEditProperties?.value ?? false;

  return (
    <>
      {!canEditProperties && (
        <Banner type="info" style={{ marginBottom: 16, width: '100%' }}>
          Shift timing can&apos;t be edited after creation. To change the date,
          start time, or duration, remove all openings and create a new shift.
        </Banner>
      )}
      <Form
        className="shift-request-edit-content"
        form={shiftGapEditForm}
        onFinish={() => handleSubmit()}
        layout="vertical"
      >
        <Row gutter={[8, 8]}>
          <StartDatetimeSelect
            disabled={!canEditShiftDetails || !canEditProperties}
            form={shiftGapEditForm}
            shiftConfigs={organizationUnitShiftConfigs}
            onTimeSelect={(value) => setSelectedStartTime(value)}
            startTime={selectedStartTime}
          />
        </Row>
        <ShiftDurationInput
          disabled={!canEditShiftDetails || !canEditProperties}
          shiftConfigs={organizationUnitShiftConfigs}
          startTime={selectedStartTime}
          form={shiftGapEditForm}
        />
        {tagsEnabled && (
          <Row gutter={[8, 8]}>
            <ShiftTagSelect
              defaultValue={shift?.shiftGapTags?.map((tag) => tag.id)}
              organizationUnitId={shift.organizationUnit.id}
            />
          </Row>
        )}
      </Form>

      <div style={{ marginTop: '16px' }}>
        <Row gutter={[0, 8]}>
          <Col span={3}>
            <Text secondary>Role</Text>
          </Col>
          <Col span={16}>{shift.role?.abbreviation}</Col>
        </Row>

        <Row gutter={[0, 8]}>
          <Col span={3}>
            <Text secondary>Unit</Text>
          </Col>
          <Col span={16}>{shift.organizationUnit.name}</Col>
        </Row>

        <Row>
          <Col span={3}>
            <Text secondary>CC</Text>
          </Col>
          <Col span={16}>{shift.organizationUnit.costCenter}</Col>
        </Row>
      </div>
    </>
  );
}
